import React, { useState } from 'react';
import { ArrowLeft, Calendar, DollarSign, Tag, FileText, Save, X, CheckCircle, AlertCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import { getAuth } from 'firebase/auth';

const Modal = ({ isOpen, onClose, type, title, message, showCloseButton = true }) => {
  if (!isOpen) return null;

  const isSuccess = type === 'success';
  const bgColor = isSuccess ? '#10B981' : '#EF4444';
  const Icon = isSuccess ? CheckCircle : AlertCircle;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px',
      backdropFilter: 'blur(5px)'
    }}>
      <div style={{
        background: 'white',
        borderRadius: '20px',
        padding: '32px',
        maxWidth: '400px',
        width: '100%',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
        position: 'relative',
        textAlign: 'center',
        animation: 'modalSlideIn 0.3s ease-out'
      }}>
        {showCloseButton && (
          <button
            onClick={onClose}
            style={{
              position: 'absolute',
              top: '16px',
              right: '16px',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: '8px',
              borderRadius: '50%',
              transition: 'background 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.target.style.background = '#f5f5f5';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = 'transparent';
            }}
          >
            <X size={20} color="#666" />
          </button>
        )}

        <div style={{
          width: '80px',
          height: '80px',
          borderRadius: '50%',
          background: bgColor,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 24px',
          animation: isSuccess ? 'successPulse 0.6s ease-out' : 'errorShake 0.5s ease-out'
        }}>
          <Icon size={40} color="white" />
        </div>

        <h2 style={{
          fontSize: '24px',
          fontWeight: 'bold',
          color: '#2D3748',
          margin: '0 0 12px 0'
        }}>
          {title}
        </h2>

        <p style={{
          fontSize: '16px',
          color: '#4A5568',
          lineHeight: '1.5',
          margin: '0 0 24px 0'
        }}>
          {message}
        </p>

        <button
          onClick={onClose}
          style={{
            background: bgColor,
            color: 'white',
            border: 'none',
            padding: '12px 32px',
            borderRadius: '12px',
            fontSize: '16px',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            minWidth: '120px'
          }}
          onMouseEnter={(e) => {
            e.target.style.transform = 'translateY(-2px)';
            e.target.style.boxShadow = `0 6px 20px ${bgColor}40`;
          }}
          onMouseLeave={(e) => {
            e.target.style.transform = 'translateY(0)';
            e.target.style.boxShadow = 'none';
          }}
        >
          {isSuccess ? 'Great!' : 'Try Again'}
        </button>
      </div>

      <style jsx>{`
        @keyframes modalSlideIn {
          from {
            opacity: 0;
            transform: translateY(-50px) scale(0.9);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }

        @keyframes successPulse {
          0% {
            transform: scale(0.8);
            opacity: 0;
          }
          50% {
            transform: scale(1.1);
          }
          100% {
            transform: scale(1);
            opacity: 1;
          }
        }

        @keyframes errorShake {
          0%, 100% {
            transform: translateX(0);
          }
          25% {
            transform: translateX(-5px);
          }
          75% {
            transform: translateX(5px);
          }
        }
      `}</style>
    </div>
  );
};

const AddExpensePage = () => {
  const [formData, setFormData] = useState({
    type: 'expense',
    amount: '',
    category: '',
    description: '',
    date: new Date().toISOString().split('T')[0],
    paymentMethod: 'cash'
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [modal, setModal] = useState({
    isOpen: false,
    type: 'success',
    title: '',
    message: ''
  });

  const navigate = useNavigate();

  const categories = [
    { value: 'food', label: 'Food & Dining', color: '#FF6B6B' },
    { value: 'transport', label: 'Transportation', color: '#4ECDC4' },
    { value: 'shopping', label: 'Shopping', color: '#45B7D1' },
    { value: 'entertainment', label: 'Entertainment', color: '#96CEB4' },
    { value: 'health', label: 'Healthcare', color: '#FFEAA7' },
    { value: 'utilities', label: 'Utilities', color: '#DDA0DD' },
    { value: 'education', label: 'Education', color: '#98D8C8' },
    { value: 'other', label: 'Other', color: '#F7DC6F' }
  ];

  const paymentMethods = [
    { value: 'cash', label: 'Cash' },
    { value: 'card', label: 'Credit Card' },
    { value: 'debit', label: 'Debit Card' },
    { value: 'digital', label: 'Digital Wallet' }
  ];

  const auth = getAuth();
  const user = auth.currentUser;

  if (!user) {
    setModal({
      isOpen: true,
      type: 'error',
      title: 'Authentication Required',
      message: 'You must be logged in to add an expense. Please log in and try again.'
    });
    return (
      <Modal
        isOpen={modal.isOpen}
        onClose={() => navigate('/login')}
        type={modal.type}
        title={modal.title}
        message={modal.message}
        showCloseButton={false}
      />
    );
  }


  const goToDashBoard = () => {
    navigate('/dashboard');
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      newErrors.amount = 'Please enter a valid amount';
    }

    if (!formData.category && formData.type === 'expense') {
      newErrors.category = 'Please select a category';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Please enter a description';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsSubmitting(true);

    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1500));
    try {
      setIsSubmitting(true);

      await addDoc(collection(db, 'expenses'), {
        ...formData,
        amount: parseFloat(formData.amount),
        createdAt: serverTimestamp(),
        userEmail: user.email
      });

      // Optionally reset form or show a toast
      setModal({
        isOpen: true,
        type: 'success',
        title: `${formData.type === 'income' ? 'Income' : 'Expense'} Added Successfully!`,
        message: `Your ${formData.type === 'income' ? 'income' : 'expense'} of $${formData.amount} has been recorded successfully.`
      });

      handleReset();
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error adding document: ', err);
      } else {
        console.error('#2 Something went wrong!!');
      }
      
      setModal({
        isOpen: true,
        type: 'error',
        title: 'Failed to Add Expense',
        message: 'Something went wrong while saving your expense. Please check your connection and try again.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    setFormData({
      amount: '',
      category: '',
      description: '',
      date: new Date().toISOString().split('T')[0],
      paymentMethod: 'cash'
    });
    setErrors({});
  };

  const selectedCategory = categories.find(cat => cat.value === formData.category);

  return (
    
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      padding: '20px'
    }}>
      <Modal
        isOpen={modal.isOpen}
        type={modal.type}
        title={modal.title}
        message={modal.message}
        showCloseButton={false}
        onClose={() => navigate('/dashboard')}
      />
      {/* Header */}
      <div style={{
        maxWidth: '800px',
        margin: '0 auto',
        marginBottom: '30px'
      }}>
        <button style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          background: 'rgba(255, 255, 255, 0.1)',
          border: 'none',
          color: 'white',
          padding: '12px 20px',
          borderRadius: '12px',
          cursor: 'pointer',
          fontSize: '16px',
          fontWeight: '500',
          backdropFilter: 'blur(10px)',
          transition: 'all 0.3s ease',
          marginBottom: '20px'
        }}
          onMouseEnter={(e) => {
            e.target.style.background = 'rgba(255, 255, 255, 0.2)';
            e.target.style.transform = 'translateX(-5px)';
          }}
          onMouseLeave={(e) => {
            e.target.style.background = 'rgba(255, 255, 255, 0.1)';
            e.target.style.transform = 'translateX(0)';
          }}
          onClick={() => { goToDashBoard() }}
        >
          <ArrowLeft size={20} />
          Back to Dashboard
        </button>

        <h1 style={{
          color: 'white',
          fontSize: 'clamp(28px, 5vw, 42px)',
          fontWeight: 'bold',
          margin: '0',
          textShadow: '0 2px 4px rgba(0,0,0,0.3)'
        }}>
          Add New Expense
        </h1>
        <p style={{
          color: 'rgba(255, 255, 255, 0.8)',
          fontSize: '18px',
          margin: '8px 0 0 0'
        }}>
          Track your spending with detailed categorization
        </p>
      </div>

      {/* Main Form Container */}
      <div style={{
        maxWidth: '800px',
        margin: '0 auto',
        background: 'rgba(255, 255, 255, 0.95)',
        borderRadius: '24px',
        padding: 'clamp(20px, 4vw, 40px)',
        backdropFilter: 'blur(20px)',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.1), 0 8px 25px rgba(0, 0, 0, 0.08)',
        border: '1px solid rgba(255, 255, 255, 0.2)'
      }}>
        <form onSubmit={handleSubmit}>
          {/* Amount Section */}
          <div style={{ marginBottom: '32px' }}>
            <label style={{
              display: 'block',
              fontSize: '16px',
              fontWeight: '600',
              color: '#2D3748',
              marginBottom: '12px'
            }}>
              Transaction Type
            </label>
            <div style={{ display: 'flex', gap: '12px' }}>
              {['expense', 'income'].map((type) => (
                <button
                  key={type}
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, type, category: type === 'income' ? '' : prev.category }))}
                  style={{
                    padding: '12px 20px',
                    borderRadius: '12px',
                    border: '2px solid #E2E8F0',
                    background: formData.type === type ? (type === 'income' ? '#10B981' : '#EF4444') : '#F7FAFC',
                    color: formData.type === type ? 'white' : '#2D3748',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease'
                  }}
                >
                  {type.charAt(0).toUpperCase() + type.slice(1)}
                </button>
              ))}
            </div>
          </div>
          <div style={{ marginBottom: '32px' }}>
            <label style={{
              display: 'block',
              fontSize: '16px',
              fontWeight: '600',
              color: '#2D3748',
              marginBottom: '12px'
            }}>
              <DollarSign size={20} style={{ display: 'inline', marginRight: '8px', verticalAlign: 'middle' }} />
              Amount
            </label>
            <div style={{ position: 'relative' }}>
              <span style={{
                position: 'absolute',
                left: '20px',
                top: '50%',
                transform: 'translateY(-50%)',
                fontSize: '24px',
                fontWeight: 'bold',
                color: '#4A5568',
                zIndex: 1
              }}>$</span>
              <input
                type="number"
                name="amount"
                min={0}
                value={formData.amount}
                onChange={handleInputChange}
                placeholder="0.00"
                step="0.01"
                onKeyDown={(e) => {
                  if (['-', '+', 'e', 'E'].includes(e.key)) {
                    e.preventDefault(); // Block minus, plus, and exponential notation
                  }
                }}
                style={{
                  width: '100%',
                  padding: '20px 20px 20px 45px',
                  fontSize: '24px',
                  fontWeight: 'bold',
                  border: errors.amount ? '2px solid #E53E3E' : '2px solid #E2E8F0',
                  borderRadius: '16px',
                  outline: 'none',
                  transition: 'all 0.3s ease',
                  background: '#F7FAFC',
                  boxSizing: 'border-box'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#667eea';
                  e.target.style.background = '#fff';
                  e.target.style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
                }}
                onBlur={(e) => {
                  if (!errors.amount) {
                    e.target.style.borderColor = '#E2E8F0';
                    e.target.style.background = '#F7FAFC';
                    e.target.style.boxShadow = 'none';
                  }
                }}
              />
            </div>
            {errors.amount && (
              <p style={{ color: '#E53E3E', fontSize: '14px', margin: '8px 0 0 0' }}>
                {errors.amount}
              </p>
            )}
          </div>

          {/* Category Selection */}
          <div style={{ marginBottom: '32px' }}>
            <label style={{
              display: 'block',
              fontSize: '16px',
              fontWeight: '600',
              color: '#2D3748',
              marginBottom: '12px'
            }}>
              <Tag size={20} style={{ display: 'inline', marginRight: '8px', verticalAlign: 'middle' }} />
              Category
            </label>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
              gap: '12px'
            }}>
              {categories.map((category) => (
                <div
                  key={category.value}
                  onClick={() => handleInputChange({ target: { name: 'category', value: category.value } })}
                  style={{
                    padding: '16px',
                    borderRadius: '12px',
                    border: formData.category === category.value ? `2px solid ${category.color}` : '2px solid #E2E8F0',
                    background: formData.category === category.value ? `${category.color}15` : '#F7FAFC',
                    cursor: formData.type === 'income' ? 'not-allowed' : 'pointer',
                    transition: 'all 0.3s ease',
                    textAlign: 'center',
                    opacity: formData.type === 'income' ? '0.3' : '1',
                    pointerEvents: formData.type === 'income' ? 'none' : 'auto'
                  }}
                  onMouseEnter={(e) => {
                    if (formData.category !== category.value) {
                      e.target.style.background = '#fff';
                      e.target.style.borderColor = category.color;
                      e.target.style.transform = 'translateY(-2px)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (formData.category !== category.value) {
                      e.target.style.background = '#F7FAFC';
                      e.target.style.borderColor = '#E2E8F0';
                      e.target.style.transform = 'translateY(0)';
                    }
                  }}
                >
                  <div style={{
                    width: '12px',
                    height: '12px',
                    borderRadius: '50%',
                    background: category.color,
                    margin: '0 auto 8px',
                  }} />
                  <span style={{
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#2D3748'
                  }}>
                    {category.label}
                  </span>
                </div>
              ))}
            </div>
            {errors.category && (
              <p style={{ color: '#E53E3E', fontSize: '14px', margin: '8px 0 0 0' }}>
                {errors.category}
              </p>
            )}
          </div>

          {/* Description and Date Row */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '2fr 1fr',
            gap: '24px',
            marginBottom: '32px'
          }}>
            {/* Description */}
            <div>
              <label style={{
                display: 'block',
                fontSize: '16px',
                fontWeight: '600',
                color: '#2D3748',
                marginBottom: '12px'
              }}>
                <FileText size={20} style={{ display: 'inline', marginRight: '8px', verticalAlign: 'middle' }} />
                Description
              </label>
              <input
                type="text"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder={formData.type === 'income' ? "What is the source of your income?" : "What did you spend on?"}
                style={{
                  width: '100%',
                  padding: '16px 20px',
                  fontSize: '16px',
                  border: errors.description ? '2px solid #E53E3E' : '2px solid #E2E8F0',
                  borderRadius: '12px',
                  outline: 'none',
                  transition: 'all 0.3s ease',
                  background: '#F7FAFC',
                  boxSizing: 'border-box'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#667eea';
                  e.target.style.background = '#fff';
                  e.target.style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
                }}
                onBlur={(e) => {
                  if (!errors.description) {
                    e.target.style.borderColor = '#E2E8F0';
                    e.target.style.background = '#F7FAFC';
                    e.target.style.boxShadow = 'none';
                  }
                }}
              />
              {errors.description && (
                <p style={{ color: '#E53E3E', fontSize: '14px', margin: '8px 0 0 0' }}>
                  {errors.description}
                </p>
              )}
            </div>

            {/* Date */}
            <div>
              <label style={{
                display: 'block',
                fontSize: '16px',
                fontWeight: '600',
                color: '#2D3748',
                marginBottom: '12px'
              }}>
                <Calendar size={20} style={{ display: 'inline', marginRight: '8px', verticalAlign: 'middle' }} />
                Date
              </label>
              <input
                type="date"
                name="date"
                value={formData.date}
                onChange={handleInputChange}
                style={{
                  width: '100%',
                  padding: '16px 20px',
                  fontSize: '16px',
                  border: '2px solid #E2E8F0',
                  borderRadius: '12px',
                  outline: 'none',
                  transition: 'all 0.3s ease',
                  background: '#F7FAFC',
                  boxSizing: 'border-box'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#667eea';
                  e.target.style.background = '#fff';
                  e.target.style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#E2E8F0';
                  e.target.style.background = '#F7FAFC';
                  e.target.style.boxShadow = 'none';
                }}
              />
            </div>
          </div>

          {/* Payment Method */}
          <div style={{ marginBottom: '40px' }}>
            <label style={{
              display: 'block',
              fontSize: '16px',
              fontWeight: '600',
              color: '#2D3748',
              marginBottom: '12px'
            }}>
              Payment Method
            </label>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
              gap: '12px'
            }}>
              {paymentMethods.map((method) => (
                <div
                  key={method.value}
                  onClick={() => handleInputChange({ target: { name: 'paymentMethod', value: method.value } })}
                  style={{
                    padding: '16px 20px',
                    borderRadius: '12px',
                    border: formData.paymentMethod === method.value ? '2px solid #667eea' : '2px solid #E2E8F0',
                    background: formData.paymentMethod === method.value ? '#667eea15' : '#F7FAFC',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    textAlign: 'center',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}
                  onMouseEnter={(e) => {
                    if (formData.paymentMethod !== method.value) {
                      e.target.style.background = '#fff';
                      e.target.style.borderColor = '#667eea';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (formData.paymentMethod !== method.value) {
                      e.target.style.background = '#F7FAFC';
                      e.target.style.borderColor = '#E2E8F0';
                    }
                  }}
                >
                  {method.label}
                </div>
              ))}
            </div>
          </div>

          {/* Action Buttons */}
          <div style={{
            display: 'flex',
            gap: '16px',
            flexWrap: 'wrap'
          }}>
            <button
              type="submit"
              disabled={isSubmitting}
              style={{
                flex: '1',
                minWidth: '200px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '12px',
                padding: '18px 32px',
                background: isSubmitting ? '#A0AEC0' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '16px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                transition: 'all 0.3s ease',
                boxShadow: '0 4px 15px rgba(102, 126, 234, 0.3)'
              }}
              onMouseEnter={(e) => {
                if (!isSubmitting) {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 6px 20px rgba(102, 126, 234, 0.4)';
                }
              }}
              onMouseLeave={(e) => {
                if (!isSubmitting) {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 4px 15px rgba(102, 126, 234, 0.3)';
                }
              }}
            >
              <Save size={20} />
              {isSubmitting ? 'Adding Expense...' : 'Add Expense'}
            </button>

            <button
              type="button"
              onClick={handleReset}
              style={{
                flex: '0 0 auto',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                padding: '18px 24px',
                background: 'transparent',
                color: '#4A5568',
                border: '2px solid #E2E8F0',
                borderRadius: '16px',
                fontSize: '16px',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.borderColor = '#CBD5E0';
                e.target.style.background = '#F7FAFC';
              }}
              onMouseLeave={(e) => {
                e.target.style.borderColor = '#E2E8F0';
                e.target.style.background = 'transparent';
              }}
            >
              <X size={18} />
              Reset
            </button>
          </div>
        </form>

        {/* Summary Card */}
        {(formData.amount || formData.category || formData.description) && (
          <div style={{
            marginTop: '32px',
            padding: '24px',
            background: formData.type === 'income'
              ? 'linear-gradient(135deg, #10B981 0%, #34D399 100%)'
              : 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
            borderRadius: '16px',
            color: 'white'
          }}>
            <h3 style={{
              margin: '0 0 16px 0',
              fontSize: '18px',
              fontWeight: '600'
            }}>
              {formData.type === 'income' ? 'Income Preview' : 'Expense Preview'}
            </h3>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', alignItems: 'center' }}>
              {formData.amount && (
                <span style={{ fontSize: '24px', fontWeight: 'bold' }}>
                  ${formData.amount}
                </span>
              )}
              {formData.type === 'expense' && selectedCategory && (
                <span style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  background: 'rgba(255, 255, 255, 0.2)',
                  padding: '8px 16px',
                  borderRadius: '20px',
                  fontSize: '14px'
                }}>
                  <div style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    background: 'white'
                  }} />
                  {selectedCategory.label}
                </span>
              )}
              {formData.description && (
                <span style={{
                  fontStyle: 'italic',
                  opacity: 0.9
                }}>
                  "{formData.description}"
                </span>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AddExpensePage;